import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { OperationStatusSchema, WorkOrderPrioritySchema, WorkOrderStatusSchema, } from './manufacturing.js';
import { pagedQuery, pagedResponse, PickerIdsSchema } from './pagination.js';
import { MessageSchema, QtyStringSchema, UnitPrecisionSchema } from './primitives.js';
const c = initContract();
/**
 * MFG-02 — WORK ORDERS. One production job from start to finish.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * THE SHAPE OF THIS CONTRACT IS OWNER RULING R1, AND IT IS WORTH READING ONCE.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * The prototype drew TWO ways to finish a job: a stepped flow with its own
 * checks, and a single green button that "wrote the whole job in one click —
 * setting the reserved flag with nothing behind it, skipping the shortage
 * check, and logging the full target as good with scrap zero" (§9). R1 removes
 * it. There is one flow, and this contract has exactly the operations that
 * flow needs:
 *
 *   reserve  →  start (through the shortage check)  →  logOutput  →  complete
 *
 * There is NO operation on this router that finishes a job in one call, and
 * that absence is the requirement (BR-MFG-02-23: "There must be exactly one
 * code path that writes a work order's output").
 *
 * The convenience the ruling DOES allow is `completionPlan` — a GET that
 * writes nothing and returns the values a quick completion would propose:
 * reserved quantities from required, the good quantity from the REMAINING
 * quantity (BR-MFG-02-14a, never the full target), scrap from zero, and
 * whether the shortage modal is going to open. The user sees them, may change
 * them, and then the same four operations above do the writing. §9 states the
 * line in one sentence: "pre-fill, never skip. A value a user did not supply
 * must at least be a value they saw and could have changed."
 */
// ── Shapes ──────────────────────────────────────────────────────────────────
export const WorkOrderComponentSchema = z
    .object({
    id: z.uuid(),
    lineNo: z.number().int(),
    itemId: z.uuid(),
    itemCode: z.string(),
    itemName: z.string(),
    unitSymbol: z.string(),
    /**
     * `N-4` — the decimals THIS component's own unit allows.
     * `work_order_components.unit_id` is per line (a job may consume grams of
     * something stocked in kilograms), so this follows the line, not the
     * finished item — `BR-MD-03-04d`'s rule in PART 9's shape.
     */
    unitPrecision: UnitPrecisionSchema,
    /** Line 2 of field 15: `SKU · unit cost`, for example `RM-302 · 2.10`. */
    snapshotUnitCost: z.string(),
    /** Field 16: the stock balance right now, in the job's warehouse. */
    onHand: z.string(),
    /** Field 17 / BR-MFG-02-03: ceil(bom qty incl. scrap × qty to produce). */
    requiredQty: z.string(),
    /** Field 18: what is set aside. Reserving posts NOTHING (§9.5). */
    reservedQty: z.string(),
    /**
     * Field 19 — NOT STORED ON THE JOB. The net sum of the consumption ledger
     * rows for this work order and item, corrections included (BR-MFG-02-12).
     */
    consumedQty: z.string(),
    isOptional: z.boolean(),
    /** Field 20: shown when on-hand < required. Carries the shortfall amount. */
    shortBy: z.string().nullable(),
    /** Field 21 / BR-MFG-02-10: permanent once applied. */
    substitutedFromItemName: z.string().nullable(),
    substitutionReason: z.string().nullable(),
    /** §5.3 [decided at build time]: hidden when the component has no approved substitute. */
    canSubstitute: z.boolean(),
    bomQty: z.string(),
    scrapPct: z.string(),
})
    .strict();
export const WorkOrderOperationSchema = z
    .object({
    id: z.uuid(),
    seq: z.number().int(),
    name: z.string(),
    workstation: z.string().nullable(),
    minutesPerUnit: z.number().int(),
    /**
     * Field 24, BUILT THE CORRECTED WAY. §5.4: the shipped code showed the
     * recipe's per-unit minutes unchanged, so the screen compared 32 planned
     * minutes against a real batch figure of 1,730 "and the variance is wrong
     * by a factor of the batch size". This is `recipe minutes × qty to
     * produce` — 12 × 50 = 600.
     */
    plannedMinutes: z.number().int(),
    actualMinutes: z.number().int(),
    operator: z.string().nullable(),
    status: OperationStatusSchema,
})
    .strict();
/** §5.5's required addition: one row per logged session, reversible, never edited. */
export const OutputLogSchema = z
    .object({
    id: z.uuid(),
    goodQty: z.string(),
    scrapQty: z.string(),
    /** BR-MFG-02-25: 'prefilled' is a quick completion; 'typed' is a counted one. */
    entryMode: z.enum(['typed', 'prefilled', 'reversal']),
    reversesLogId: z.uuid().nullable(),
    /** True once some later row reverses this one — the Reverse button then goes. */
    isReversed: z.boolean(),
    loggedOn: z.string(),
    loggedAt: z.string(),
    loggedByName: z.string().nullable(),
})
    .strict();
/** §5.6 — the Actual vs planned cost card. Five rows, and they must reconcile. */
export const WorkOrderCostSchema = z
    .object({
    /** Field 39: frozen from the snapshot, EXCLUDING overhead (OQ-9.7-8). */
    plannedCost: z.string(),
    /** Field 40: Σ(consumed × the cost AT ISSUE), from the ledger. */
    actualMaterial: z.string(),
    /** Field 41: total actual minutes × the SNAPSHOT rate (BR-MFG-02-20). */
    actualLabor: z.string(),
    actualCost: z.string(),
    /** Field 43 / BR-MFG-02-21: `actual − planned`; positive is unfavourable. */
    variance: z.string(),
    /** §8.3: shown instead of a blank card when nothing has been logged. */
    hasOutput: z.boolean(),
    /**
     * §12's postings 3–5, which the cost card does not show but an accountant
     * closing the job needs. The last three exist because §12 asks for them by
     * name: "That leftover of 90.61 has two parts, and the screen must let an
     * accountant name both: 86.94 is the two scrapped lamps at 43.47 each, and
     * 3.67 is the job running over on parts and time. The two add to 90.61
     * exactly, and a build that cannot produce that split has got something
     * wrong."
     */
    overheadApplied: z.string(),
    /** Good quantity × the recipe's full per-unit cost. Scrap enters stock nowhere. */
    outputValue: z.string(),
    /** What is left in work in progress: material + labour + overhead − output. */
    closingVariance: z.string(),
    /** The scrapped units at that same per-unit cost. */
    scrapValue: z.string(),
    /** The rest: the job running over on parts and time. */
    overrunValue: z.string(),
    /** BR-MFG-02-16: the cost good output is received at, from the snapshot. */
    outputUnitCost: z.string(),
})
    .strict();
const workOrderShape = z
    .object({
    id: z.uuid(),
    /** Field 1: WO-YYYY-####, assigned at CREATION. */
    code: z.string(),
    status: WorkOrderStatusSchema,
    /** Field 6: the amber strip's real reason (§5.1's required addition). */
    exceptionReason: z.string().nullable(),
    finishedItemId: z.uuid(),
    finishedItemName: z.string(),
    finishedItemCode: z.string(),
    /**
     * `N-4` for the JOB's own quantities — target, good, scrap, produced and
     * remaining. `work_orders` stores no unit, so it is the finished item's
     * STOCK unit: the shelf the good output is received onto
     * (`BR-MFG-02-16`). The component rows carry their own.
     */
    outputUnitSymbol: z.string(),
    outputUnitPrecision: UnitPrecisionSchema,
    bomVersionId: z.uuid(),
    /** Field 4: `BOM v3`. §3 [decided at build time]: the id above makes it a real link. */
    bomLabel: z.string(),
    qtyToProduce: z.string(),
    warehouseId: z.uuid(),
    warehouseName: z.string(),
    plannedStart: z.string(),
    plannedEnd: z.string(),
    linkedSalesOrder: z.string().nullable(),
    priority: WorkOrderPrioritySchema,
    materialsReserved: z.boolean(),
    /** Whether ANY component is short right now — drives the banner and the modal. */
    hasShortage: z.boolean(),
    goodQty: z.string(),
    scrapQty: z.string(),
    /** Field 33: `good + scrap`. */
    producedQty: z.string(),
    /** Field 36: `qty to produce − produced`, NEVER below zero. On the button's face. */
    remainingQty: z.string(),
    /** Field 29: `produced ÷ target × 100`, whole number. */
    progressPct: z.number().int(),
    /** Field 37: stamped at the FIRST output log, never re-stamped. */
    batchNo: z.string().nullable(),
    /** Field 38: frozen at completion (BR-MFG-02-17). */
    yieldPct: z.string().nullable(),
    backflush: z.boolean(),
    laborRate: z.string(),
    overheadPct: z.string(),
    components: WorkOrderComponentSchema.array(),
    operations: WorkOrderOperationSchema.array(),
    /** Field 27/28: `Planned: 1600m · Actual: 1730m`. */
    plannedMinutesTotal: z.number().int(),
    actualMinutesTotal: z.number().int(),
    outputLogs: OutputLogSchema.array(),
    cost: WorkOrderCostSchema,
    completedAt: z.string().nullable(),
    closedAt: z.string().nullable(),
    createdAt: z.string(),
    updatedAt: z.string(),
})
    .strict();
/**
 * What a quick completion would PROPOSE. A GET: it writes nothing, reserves
 * nothing and sets no flag. Every number here is a suggestion the user is
 * shown and may change before the real operations run (§9, BR-MFG-02-23/-24).
 */
export const CompletionPlanSchema = z
    .object({
    workOrderId: z.uuid(),
    /** The steps still outstanding, in order, so the screen can say what will happen. */
    steps: z.enum(['reserve', 'start', 'log', 'complete']).array(),
    /** Reserved-from-required, capped at on-hand — what `reserve` would write. */
    proposedReservations: z
        .object({
        componentId: z.uuid(),
        itemName: z.string(),
        requiredQty: z.string(),
        onHand: z.string(),
        proposedQty: z.string(),
        /** BR-MFG-02-05's sentence, when on-hand cannot cover the requirement. */
        cappedNote: z.string().nullable(),
    })
        .array(),
    /** BR-MFG-02-07: true means the Material shortage modal opens FIRST. */
    hasShortage: z.boolean(),
    shortItemNames: z.string().array(),
    /** BR-MFG-02-14a: the REMAINING quantity, never the full target. */
    proposedGoodQty: z.string(),
    /** BR-MFG-02-24: proposed as zero and SHOWN, never assumed behind the user. */
    proposedScrapQty: z.string(),
})
    .strict();
export const CreateWorkOrderRequestSchema = z
    .object({
    finishedItemId: z.uuid(),
    qtyToProduce: QtyStringSchema,
    warehouseId: z.uuid(),
    plannedStart: z.iso.date(),
    plannedEnd: z.iso.date(),
    linkedSalesOrder: z.string().trim().max(32).nullable().optional(),
    priority: WorkOrderPrioritySchema.optional(),
    /** Absent = the organization setting's value (field 44). */
    backflush: z.boolean().optional(),
})
    .strict();
export const UpdateWorkOrderRequestSchema = z
    .object({
    qtyToProduce: QtyStringSchema.optional(),
    /** BR-MFG-02-26: locks at start. */
    warehouseId: z.uuid().optional(),
    plannedStart: z.iso.date().optional(),
    plannedEnd: z.iso.date().optional(),
    linkedSalesOrder: z.string().trim().max(32).nullable().optional(),
    priority: WorkOrderPrioritySchema.optional(),
    backflush: z.boolean().optional(),
})
    .strict();
export const StartProductionRequestSchema = z
    .object({
    /**
     * BR-MFG-02-06/-07: a shortage never blocks, but it never passes silently
     * either. When any non-optional component is short the server refuses
     * unless this is true — which is the server-side half of §8.5's "Start
     * anyway" button, and what makes "every route into production passes this
     * modal" true of the API and not only of the screen.
     */
    acknowledgeShortage: z.boolean().optional(),
})
    .strict();
export const LogOutputRequestSchema = z
    .object({
    /** Field 34. Both are inputs; NEITHER is assumed (BR-MFG-02-24). */
    goodQty: z.string().regex(/^\d{1,12}(\.\d{1,3})?$/),
    /** Field 35. Zero is a legal value a person SAW; it is never a default. */
    scrapQty: z.string().regex(/^\d{1,12}(\.\d{1,3})?$/),
    /**
     * BR-MFG-02-25. 'typed' — a person keyed these figures. 'prefilled' — the
     * quick-complete convenience proposed them and the person confirmed them
     * unchanged. The server cannot tell a retyped number from a confirmed one,
     * so the caller declares it and the ledger keeps the declaration for the
     * life of the record. Defaults to 'typed'.
     */
    entryMode: z.enum(['typed', 'prefilled']).optional(),
    /** The document date the movements and postings carry. Defaults to today. */
    loggedOn: z.iso.date().optional(),
})
    .strict();
export const ReserveRequestSchema = z
    .object({
    /**
     * Absent = the bulk Reserve materials button: every line filled from
     * Required and capped at on-hand. Present = the per-line edit §5.3 field
     * 18 allows ("editable per line afterwards, so partial or over-reservation
     * by hand is possible").
     */
    lines: z
        .object({ componentId: z.uuid(), reservedQty: z.string() })
        .strict()
        .array()
        .optional(),
})
    .strict();
export const SubstituteRequestSchema = z
    .object({
    componentId: z.uuid(),
    replacementItemId: z.uuid(),
    /** §8.4: the reason carries a RED ASTERISK. BR-MFG-02-08. */
    reason: z.string().trim().min(1).max(300),
})
    .strict();
export const UpdateOperationRequestSchema = z
    .object({
    /** Field 22: advance one step. §5.4's decision — it must not wrap past Done. */
    status: OperationStatusSchema.optional(),
    actualMinutes: z.number().int().min(0).max(999999).optional(),
    operator: z.string().trim().max(80).nullable().optional(),
})
    .strict();
export const HoldRequestSchema = z
    .object({ reason: z.string().trim().min(1).max(200) })
    .strict();
/**
 * ── Why this one schema is declared and not inferred ────────────────────────
 * `WorkOrderSchema` answers 19 of this module's routes. Annotating it as
 * `z.ZodType<WorkOrderResponse>` writes the shape ONCE and lets every route
 * reference it by name, which keeps this module's emitted declaration a few
 * kilobytes instead of a hundred. Runtime validation is unchanged: the object
 * schema above is still what parses.
 */
export const WorkOrderSchema = workOrderShape;
const woParams = z.object({ workOrderId: z.uuid() }).strict();
const errors = {
    400: MessageSchema,
    401: MessageSchema,
    403: MessageSchema,
    404: MessageSchema,
    409: MessageSchema,
};
export const workOrdersContract = c.router({
    list: {
        method: 'GET',
        path: '/api/manufacturing/work-orders',
        query: pagedQuery({
            status: WorkOrderStatusSchema.optional(),
            finishedItemId: z.uuid().optional(),
            /** Open jobs = anything not closed or cancelled (the MRP incoming read). */
            openOnly: z.enum(['true']).optional(),
            /**
             * GEN-50's first read — the printed job number OR the finished item's
             * name, which are the two halves MFG-04's picker prints on its row. A
             * search that matched only the number would refuse the text the reader
             * can see beside it.
             */
            q: z.string().trim().max(120).optional(),
            /**
             * GEN-50's second read — the job a voucher or a ledger filter is
             * ALREADY holding, resolved by id and by nothing else. It ignores
             * `status` and `openOnly` deliberately: a correction voucher against a
             * CLOSED job still has to show which job it corrects.
             */
            ids: PickerIdsSchema.optional(),
        }),
        responses: {
            200: pagedResponse('workOrders', WorkOrderSchema),
            401: MessageSchema,
            403: MessageSchema,
        },
        summary: 'Work orders (manufacturing.view), PAGED (NFR-42) — every filter in SQL, `openOnly` included: it is BR-MFG-03-05\u2019s three open statuses as an IN, not a post-filter. `q` and `ids` are GEN-50’s two picker reads.',
    },
    get: {
        method: 'GET',
        path: '/api/manufacturing/work-orders/:workOrderId',
        pathParams: woParams,
        responses: { 200: WorkOrderSchema, 401: MessageSchema, 403: MessageSchema, 404: MessageSchema },
        summary: 'One job, with its live on-hand, consumed and cost figures.',
    },
    create: {
        method: 'POST',
        path: '/api/manufacturing/work-orders',
        body: CreateWorkOrderRequestSchema,
        responses: { 201: WorkOrderSchema, ...errors },
        // DEEP-COPIES the active recipe (BR-MFG-02-02) and refuses an item with
        // none (BR-MFG-02-01).
        summary: 'New job (manufacturing.create).',
    },
    update: {
        method: 'PATCH',
        path: '/api/manufacturing/work-orders/:workOrderId',
        pathParams: woParams,
        body: UpdateWorkOrderRequestSchema,
        responses: { 200: WorkOrderSchema, ...errors },
        // BR-MFG-02-26: freely editable while Draft; the warehouse locks at
        // start; the quantity is floored by what has been produced
        // (BR-MFG-02-11); a Completed job is not edited at all (BR-MFG-02-18).
        summary: 'Edit (manufacturing.edit).',
    },
    remove: {
        method: 'DELETE',
        path: '/api/manufacturing/work-orders/:workOrderId',
        pathParams: woParams,
        body: c.noBody(),
        responses: {
            204: c.noBody(),
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Delete a DRAFT (manufacturing.delete).',
    },
    reserve: {
        method: 'POST',
        path: '/api/manufacturing/work-orders/:workOrderId/reserve',
        pathParams: woParams,
        body: ReserveRequestSchema,
        responses: { 200: WorkOrderSchema, ...errors },
        // Fills Reserved from Required capped at on-hand, raises the Reserved
        // figure on each stock balance, moves the job to Scheduled. Posts NO
        // movement and NO journal entry (BR-MFG-02-04, §9.5). THE ONLY thing in
        // the module that sets materials-reserved (BR-MFG-02-24).
        summary: 'Step 1 — reserve materials.',
    },
    unreserve: {
        method: 'POST',
        path: '/api/manufacturing/work-orders/:workOrderId/unreserve',
        pathParams: woParams,
        body: c.noBody(),
        responses: { 200: WorkOrderSchema, ...errors },
        summary: 'Un-reserve — reverses it completely, back to Draft.',
    },
    start: {
        method: 'POST',
        path: '/api/manufacturing/work-orders/:workOrderId/start',
        pathParams: woParams,
        body: StartProductionRequestSchema,
        responses: { 200: WorkOrderSchema, ...errors },
        // A short job is refused with the Material shortage sentence until
        // acknowledgeShortage is true (BR-MFG-02-06/-07/-23). The check has no
        // bypass, because there is no other route into production.
        summary: 'Step 2 — start, through the shortage check.',
    },
    logOutput: {
        method: 'POST',
        path: '/api/manufacturing/work-orders/:workOrderId/output',
        pathParams: woParams,
        body: LogOutputRequestSchema,
        responses: { 200: WorkOrderSchema, ...errors },
        // THE ONLY CODE PATH THAT WRITES A JOB'S OUTPUT (BR-MFG-02-23). Adds
        // the session, stamps the batch on the FIRST log, and — when backflush
        // is on — recalculates each component to
        // min(reserved, round(required x progress)) from CUMULATIVE output,
        // posting the consumption vouchers (BR-MFG-02-19).
        summary: 'Step 3 — log the production that was counted.',
    },
    reverseOutput: {
        method: 'POST',
        path: '/api/manufacturing/work-orders/:workOrderId/output/:logId/reverse',
        pathParams: z.object({ workOrderId: z.uuid(), logId: z.uuid() }).strict(),
        body: c.noBody(),
        responses: { 200: WorkOrderSchema, ...errors },
        // §5.5's Reverse action: posts an OPPOSITE session (owner ruling R8).
        // The mis-keyed row stays; both are visible for ever.
        summary: 'Reverse a logged session.',
    },
    complete: {
        method: 'POST',
        path: '/api/manufacturing/work-orders/:workOrderId/complete',
        pathParams: woParams,
        body: c.noBody(),
        responses: { 200: WorkOrderSchema, ...errors },
        // Allowed with a remaining quantity above zero (BR-MFG-02-14). Freezes
        // the yield, releases every remaining reservation, receives GOOD output
        // only at the planned unit cost (BR-MFG-02-16), posts labour then
        // overhead (§12 postings 2-4). It logs NOTHING itself: what was made was
        // logged in step 3, which is what BR-MFG-02-24 is protecting.
        summary: 'Step 4 — mark complete.',
    },
    close: {
        method: 'POST',
        path: '/api/manufacturing/work-orders/:workOrderId/close',
        pathParams: woParams,
        body: c.noBody(),
        responses: { 200: WorkOrderSchema, ...errors },
        // §7 [built — the prototype had the stage and no control]. Posts the
        // manufacturing variance from work in progress (§12 posting 5), which is
        // what leaves WIP at exactly zero, and locks the job for ever.
        summary: 'Close the job (manufacturing.approve).',
    },
    cancel: {
        method: 'POST',
        path: '/api/manufacturing/work-orders/:workOrderId/cancel',
        pathParams: woParams,
        body: c.noBody(),
        responses: { 200: WorkOrderSchema, ...errors },
        // §6 [built]. Releases reservations. Refused once anything has been
        // consumed or produced (BR-MFG-02-13).
        summary: 'Cancel a Draft or Scheduled job.',
    },
    hold: {
        method: 'POST',
        path: '/api/manufacturing/work-orders/:workOrderId/hold',
        pathParams: woParams,
        body: HoldRequestSchema,
        responses: { 200: WorkOrderSchema, ...errors },
        // §7 [built — the prototype had the banner and no action]. Parts stay
        // reserved and nothing is lost; the banner carries a REAL reason.
        summary: 'Put the job on hold, with a reason.',
    },
    resume: {
        method: 'POST',
        path: '/api/manufacturing/work-orders/:workOrderId/resume',
        pathParams: woParams,
        body: c.noBody(),
        responses: { 200: WorkOrderSchema, ...errors },
        summary: 'Resume — back to the stage it was held from.',
    },
    substitute: {
        method: 'POST',
        path: '/api/manufacturing/work-orders/:workOrderId/substitute',
        pathParams: woParams,
        body: SubstituteRequestSchema,
        responses: { 200: WorkOrderSchema, ...errors },
        // Replaces the component ON THIS JOB ONLY (BR-MFG-02-09), recalculates
        // Required from the same BOM quantity, re-reads on-hand and unit cost,
        // re-evaluates the shortage flag, and leaves a permanent note naming
        // what it replaced and why (BR-MFG-02-10).
        summary: '§8.4 Substitute a component.',
    },
    updateOperation: {
        method: 'PATCH',
        path: '/api/manufacturing/work-orders/:workOrderId/operations/:operationId',
        pathParams: z.object({ workOrderId: z.uuid(), operationId: z.uuid() }).strict(),
        body: UpdateOperationRequestSchema,
        responses: { 200: WorkOrderSchema, ...errors },
        // Advance the status (never past Done — §5.4), type the actual minutes,
        // name the operator.
        summary: 'The Routing tab row edit.',
    },
    completionPlan: {
        method: 'GET',
        path: '/api/manufacturing/work-orders/:workOrderId/completion-plan',
        pathParams: woParams,
        responses: {
            200: CompletionPlanSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        // THE PRE-FILL, AND ONLY THE PRE-FILL (§9, BR-MFG-02-23). A GET: it
        // reserves nothing, sets no flag and writes no output. It returns what a
        // quick completion would PROPOSE — reservations from required, good from
        // REMAINING (never the target, BR-MFG-02-14a), scrap zero SHOWN rather
        // than assumed, and whether the shortage modal will open — for the user
        // to see and change. The writing is done by reserve/start/logOutput/
        // complete, the same four operations the step-by-step flow uses.
        summary: 'What a quick completion would propose. Writes nothing.',
    },
}, { strictStatusCodes: true });
//# sourceMappingURL=work-orders.js.map